import { Injectable } from '@angular/core';
import 'rxjs/add/observable/fromPromise';
import { Headers, Http, Response } from '@angular/http';
import { AuthenticationService, Credentials, Logger } from 'app/core/index';
import { Subscription } from 'rxjs/Subscription';
import { map } from 'rxjs/operators';
import { Subject } from 'rxjs/Subject';
import { BehaviorSubject } from 'rxjs/BehaviorSubject';
import { Observable } from 'rxjs/Observable';

const log = new Logger('WithdrawService');

export interface StateInterface {
  winBalance: number;
  gameCount: number;
}

export interface WithdrawLimitsInterface {
  localBalance: number;
  contractBalance: number;
  playerWallet: number;
  availableBalance: number;
  lastState: StateInterface;
  currentState: StateInterface;
  withdrawFee: number;
  stateFee: number;
  upToDate: boolean;
  withdrawLimit: number;
  minAmount: number;
  pendingDeposits: number;
}

export interface WithdrawLimitInterface {
  availableBalance: number;
  min: number;
  max: number;
  fee: number;
  usdRate: number;
}

export interface WithdrawRequestContext {
  address: string;
  currency: string;
  amount: number;
  password: string;
}

@Injectable()
export class WithdrawService {
  private _updating: boolean = false;
  private _updated: boolean = false;
  public limits: WithdrawLimitsInterface = null;

  public withdrawRequestedSubject: Subject<boolean> = new BehaviorSubject(false);
  withdrawRequested$: Observable<boolean> = this.withdrawRequestedSubject.asObservable();

  constructor(private http: Http, private auth: AuthenticationService) {
    log.debug('service constructed.');
  }

  public withdrawLimits(currency: string): Observable<WithdrawLimitInterface> {
    const headers = new Headers();
    headers.append('Authorization', this.auth.credentials.accessToken);
    return this.http
      .get(`/api/withdraw/limit/${currency}`, { headers })
      .pipe(map((response: Response) => response.json() as WithdrawLimitInterface));
  }

  /**
   * @deprecated
   */
  public getLimits(): Promise<WithdrawLimitsInterface | Error> {
    if (this._updating) {
      return Promise.resolve(null);
    }
    this._updating = true;
    log.info('retrieving withdraw limits.');
    const headers = new Headers();
    headers.append('Authorization', this.auth.credentials.accessToken);
    return this.http
      .get('/api/withdraw/limits', { headers })
      .toPromise()
      .then((response: Response) => {
        this._updated = true;
        this._updating = false;
        const value = response.json();
        this.limits = value;
        log.debug('withdraw limits retrieved.', value);
        return this.limits;
      })
      .catch((error: Error) => {
        this._updating = false;
        this._updated = false;
        log.error(error);
        return error;
      });
  }

  /**
   * Requests withdrawal
   * @returns {Promise<Response>}
   * @param body
   */
  public requestWithdraw(body: WithdrawRequestContext) {
    log.info('withdraw requested.');
    const headers: Headers = new Headers();
    headers.append('Authorization', this.auth.credentials.accessToken);

    return this.http
      .post('/api/withdraw', body, {
        headers,
      })
      .toPromise()
      .then(data => {
        log.debug('withdraw request finished with data: ', data);
        this.withdrawRequestedSubject.next(true);
        return data;
      });
  }

  /**
   * Gets converted amount to a specified currency.
   * @param {string} from
   * @param {string} to
   * @param {number} amount
   * @returns {Promise<Error | never | Response>}
   */
  public getConvertedAmount(from: string, to: string, amount: number) {
    log.info('retrieving converted amount');
    const headers = new Headers();
    headers.append('Authorization', this.auth.credentials.accessToken);
    return this.http.get(`/api/withdraw/convert-amount?from=${from}&to=${to}&amount=${amount}`, { headers }).pipe(
      map((response: Response) => {
        return { ...response.json(), fromAmount: amount };
      })
    );
  }

  public getFullCurrencies() {
    const headers = new Headers();
    headers.append('Authorization', this.auth.credentials.accessToken);
    return this.http.get(`/api/withdraw/currencies-full`, { headers }).pipe(
      map((response: Response) => {
        return response.json().filter(currency => !!currency.enabled);
      })
    );
  }

  /**
   * Gets currency exchange limits.
   * @param {string} from
   * @param {string} to
   * @returns {Observable<any>}
   */
  public getCurrencyLimit(from: string, to: string) {
    log.info('retrieving converted amount');
    const headers = new Headers();
    headers.append('Authorization', this.auth.credentials.accessToken);
    return this.http.get(`/api/withdraw/currency-limits?from=${from}&to=${to}`, { headers }).pipe(
      map((response: Response) => {
        return response.json();
      })
    );
  }

  /**
   * @param requestId
   * @param code
   */
  public getWithdrawalRequest(requestId: string, code: string) {
    return this.http
      .get(`/api/withdraw/request?requestId=${requestId}&confirmationCode=${code}`)
      .pipe(map((response: Response) => response.json()));
  }

  /**
   * @param requestId
   * @param code
   * @param state
   */
  public confirmWithdrawalRequest(requestId: string, code: string, state: string) {
    return this.http
      .patch(`/api/withdraw/request`, {
        confirmationCode: code,
        requestId,
        state,
      })
      .pipe(map((response: Response) => response.json()));
  }
}



// WEBPACK FOOTER //
// /var/lib/jenkins/workspace/Prod_web-client/src/app/core/account/withdraw.service.ts